import React from 'react';
import { useMetaTags } from '../hooks/useMetaTags';
export function CANoticePage() {
  useMetaTags({
    title: 'CA Notice at Collection | FindOnlineDegrees',
    description: 'California Consumer Privacy Act notice for FindOnlineDegrees.org'
  });
  return <div className="min-h-screen bg-gray-100" data-id="element-602">
      {/* Hero Section */}
      <section className="w-full bg-[#1a2332] text-white py-16 px-4" data-id="element-603">
        <div className="max-w-4xl mx-auto text-center" data-id="element-604">
          <h1 className="text-3xl md:text-5xl font-bold mb-4" data-id="element-605">
            CA Notice At Collection
          </h1>
          <p className="text-gray-300 text-sm" data-id="element-606">Last updated: January 2026</p>
        </div>
      </section>

      {/* Content Section */}
      <section className="max-w-4xl mx-auto px-4 py-12" data-id="element-607">
        <div className="bg-white rounded-lg shadow-sm p-8 md:p-12 space-y-8" data-id="element-608">
          <div className="space-y-4 text-gray-700 leading-relaxed" data-id="element-609">
            <p data-id="element-610">
              This privacy notice for FindOnlineDegrees.org describes how and
              why we might collect, store, use, and/or share your information
              when you use our services, such as when you:
            </p>
            <ul className="list-disc pl-6 space-y-2" data-id="element-611">
              <li data-id="element-612">
                Visit our website, or any website of ours that links to this
                privacy notice
              </li>
              <li data-id="element-613">
                Engage with us in other related ways, including any sales,
                marketing, or events
              </li>
            </ul>
            <p className="font-semibold" data-id="element-614">
              Questions or concerns? Reading this privacy notice will help you
              understand your privacy rights and choices. If you do not agree
              with our policies and practices, please do not use our services.
            </p>
          </div>

          <div className="space-y-4 text-gray-700 leading-relaxed" data-id="element-615">
            <p data-id="element-616">
              If you are a California resident, the California Consumer Privacy
              Act ("CCPA") requires us to provide you with the following notice
              with information about:
            </p>
            <ul className="list-disc pl-6 space-y-2" data-id="element-617">
              <li data-id="element-618">
                the purpose for which we use each category of "personal
                information" (as defined in the CCPA) we collect; and
              </li>
              <li data-id="element-619">
                the categories of third parties to which we (a) disclose such
                personal information for a business purpose, (b) "share"
                personal information for "cross-context behavioral advertising,"
                and/or (c) "sell" such personal information.
              </li>
            </ul>
          </div>

          {/* Categories of Personal Information */}
          <div data-id="element-620">
            <h2 className="text-2xl font-bold text-[#3b82f6] mb-4" data-id="element-621">
              Categories of Personal Information We Collect
            </h2>
            <div className="space-y-4 text-gray-700 leading-relaxed" data-id="element-622">
              <p data-id="element-623">
                The table below describes the categories of personal information
                we have collected from California consumers in the preceding 12
                months, the sources from which we collected it, our business or
                commercial purposes for collecting it, and the categories of
                third parties to whom we disclose, share, or sell that
                information.
              </p>
            </div>

            <div className="overflow-x-auto mt-6" data-id="element-624">
              <table className="min-w-full border border-gray-300" data-id="element-625">
                <thead className="bg-gray-50" data-id="element-626">
                  <tr data-id="element-627">
                    <th className="border border-gray-300 px-4 py-3 text-left text-sm font-semibold text-gray-900" data-id="element-628">
                      Category of Personal Information
                    </th>
                    <th className="border border-gray-300 px-4 py-3 text-left text-sm font-semibold text-gray-900" data-id="element-629">
                      Examples
                    </th>
                    <th className="border border-gray-300 px-4 py-3 text-left text-sm font-semibold text-gray-900" data-id="element-630">
                      Collected?
                    </th>
                    <th className="border border-gray-300 px-4 py-3 text-left text-sm font-semibold text-gray-900" data-id="element-631">
                      Sources
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white" data-id="element-632">
                  <tr data-id="element-633">
                    <td className="border border-gray-300 px-4 py-3 text-sm" data-id="element-634">
                      <strong data-id="element-635">Identifiers</strong>
                    </td>
                    <td className="border border-gray-300 px-4 py-3 text-sm" data-id="element-636">
                      Name, email address, phone number, IP address, device ID
                    </td>
                    <td className="border border-gray-300 px-4 py-3 text-sm" data-id="element-637">
                      Yes
                    </td>
                    <td className="border border-gray-300 px-4 py-3 text-sm" data-id="element-638">
                      Directly from you via forms, automatically via cookies
                    </td>
                  </tr>
                  <tr className="bg-gray-50" data-id="element-639">
                    <td className="border border-gray-300 px-4 py-3 text-sm" data-id="element-640">
                      <strong data-id="element-641">Personal Information (CA Civil Code)</strong>
                    </td>
                    <td className="border border-gray-300 px-4 py-3 text-sm" data-id="element-642">
                      Name, contact information, education history
                    </td>
                    <td className="border border-gray-300 px-4 py-3 text-sm" data-id="element-643">
                      Yes
                    </td>
                    <td className="border border-gray-300 px-4 py-3 text-sm" data-id="element-644">
                      Directly from you via forms
                    </td>
                  </tr>
                  <tr data-id="element-645">
                    <td className="border border-gray-300 px-4 py-3 text-sm" data-id="element-646">
                      <strong data-id="element-647">Commercial Information</strong>
                    </td>
                    <td className="border border-gray-300 px-4 py-3 text-sm" data-id="element-648">
                      Records of services considered or obtained
                    </td>
                    <td className="border border-gray-300 px-4 py-3 text-sm" data-id="element-649">
                      Yes
                    </td>
                    <td className="border border-gray-300 px-4 py-3 text-sm" data-id="element-650">
                      From your interactions with our services
                    </td>
                  </tr>
                  <tr className="bg-gray-50" data-id="element-651">
                    <td className="border border-gray-300 px-4 py-3 text-sm" data-id="element-652">
                      <strong data-id="element-653">Internet/Network Activity</strong>
                    </td>
                    <td className="border border-gray-300 px-4 py-3 text-sm" data-id="element-654">
                      Browsing history, search history, interaction with website
                    </td>
                    <td className="border border-gray-300 px-4 py-3 text-sm" data-id="element-655">
                      Yes
                    </td>
                    <td className="border border-gray-300 px-4 py-3 text-sm" data-id="element-656">
                      Automatically via cookies and analytics
                    </td>
                  </tr>
                  <tr data-id="element-657">
                    <td className="border border-gray-300 px-4 py-3 text-sm" data-id="element-658">
                      <strong data-id="element-659">Geolocation Data</strong>
                    </td>
                    <td className="border border-gray-300 px-4 py-3 text-sm" data-id="element-660">
                      Approximate location based on IP address
                    </td>
                    <td className="border border-gray-300 px-4 py-3 text-sm" data-id="element-661">
                      Yes
                    </td>
                    <td className="border border-gray-300 px-4 py-3 text-sm" data-id="element-662">
                      Automatically via IP address
                    </td>
                  </tr>
                  <tr className="bg-gray-50" data-id="element-663">
                    <td className="border border-gray-300 px-4 py-3 text-sm" data-id="element-664">
                      <strong data-id="element-665">Inferences</strong>
                    </td>
                    <td className="border border-gray-300 px-4 py-3 text-sm" data-id="element-666">
                      Preferences, characteristics, behavior
                    </td>
                    <td className="border border-gray-300 px-4 py-3 text-sm" data-id="element-667">
                      Yes
                    </td>
                    <td className="border border-gray-300 px-4 py-3 text-sm" data-id="element-668">
                      Derived from your activity on our services
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>
          </div>

          {/* Business Purposes */}
          <div data-id="element-669">
            <h2 className="text-2xl font-bold text-[#3b82f6] mb-4" data-id="element-670">
              Business or Commercial Purposes for Collection
            </h2>
            <div className="space-y-4 text-gray-700 leading-relaxed" data-id="element-671">
              <p data-id="element-672">
                We collect and use personal information for the following
                purposes:
              </p>
              <ul className="list-disc pl-6 space-y-2" data-id="element-673">
                <li data-id="element-674">To provide, improve, and administer our services</li>
                <li data-id="element-675">
                  To communicate with you, including responding to inquiries
                </li>
                <li data-id="element-676">To ensure security and fraud prevention</li>
                <li data-id="element-677">To comply with legal obligations</li>
                <li data-id="element-678">To deliver targeted advertising and marketing</li>
                <li data-id="element-679">To analyze and improve our website performance</li>
                <li data-id="element-680">To connect you with educational institutions</li>
              </ul>
            </div>
          </div>

          {/* Disclosure of Personal Information */}
          <div data-id="element-681">
            <h2 className="text-2xl font-bold text-[#3b82f6] mb-4" data-id="element-682">
              Disclosure of Personal Information
            </h2>
            <div className="space-y-4 text-gray-700 leading-relaxed" data-id="element-683">
              <p data-id="element-684">
                We may disclose your personal information to the following
                categories of third parties:
              </p>
              <ul className="list-disc pl-6 space-y-2" data-id="element-685">
                <li data-id="element-686">Educational institutions and their partners</li>
                <li data-id="element-687">Service providers (analytics, hosting, marketing)</li>
                <li data-id="element-688">Advertising partners and networks</li>
                <li data-id="element-689">Legal and regulatory authorities when required</li>
                <li data-id="element-690">Business partners in connection with business transfers</li>
              </ul>
            </div>
          </div>

          {/* Your Rights */}
          <div data-id="element-691">
            <h2 className="text-2xl font-bold text-[#3b82f6] mb-4" data-id="element-692">
              Your California Privacy Rights
            </h2>
            <div className="space-y-4 text-gray-700 leading-relaxed" data-id="element-693">
              <p data-id="element-694">As a California resident, you have the following rights:</p>
              <ul className="list-disc pl-6 space-y-2" data-id="element-695">
                <li data-id="element-696">
                  <strong data-id="element-697">Right to Know:</strong> Request disclosure of the
                  categories and specific pieces of personal information we have
                  collected about you.
                </li>
                <li data-id="element-698">
                  <strong data-id="element-699">Right to Delete:</strong> Request deletion of your
                  personal information, subject to certain exceptions.
                </li>
                <li data-id="element-700">
                  <strong data-id="element-701">Right to Correct:</strong> Request correction of
                  inaccurate personal information.
                </li>
                <li data-id="element-702">
                  <strong data-id="element-703">Right to Opt-Out:</strong> Opt out of the "sale" or
                  "sharing" of your personal information for cross-context
                  behavioral advertising.
                </li>
                <li data-id="element-704">
                  <strong data-id="element-705">Right to Limit:</strong> Limit the use and disclosure
                  of sensitive personal information (if applicable).
                </li>
                <li data-id="element-706">
                  <strong data-id="element-707">Right to Non-Discrimination:</strong> You will not
                  receive discriminatory treatment for exercising your privacy
                  rights.
                </li>
              </ul>
            </div>
          </div>

          {/* How to Exercise Rights */}
          <div data-id="element-708">
            <h2 className="text-2xl font-bold text-[#3b82f6] mb-4" data-id="element-709">
              How to Exercise Your Rights
            </h2>
            <div className="space-y-4 text-gray-700 leading-relaxed" data-id="element-710">
              <p data-id="element-711">
                To exercise your rights under the CCPA, please contact us
                through the information provided on our website. We will verify
                your identity before processing your request and respond within
                the timeframes required by law.
              </p>
            </div>
          </div>

          {/* Contact Us */}
          <div data-id="element-712">
            <h2 className="text-2xl font-bold text-[#3b82f6] mb-4" data-id="element-713">
              Contact Us
            </h2>
            <div className="space-y-4 text-gray-700 leading-relaxed" data-id="element-714">
              <p data-id="element-715">
                If you have questions about this notice or wish to exercise your
                California privacy rights, please contact us through the
                information provided on our website.
              </p>
            </div>
          </div>
        </div>
      </section>
    </div>;
}